import { MediaItem, MediaType, MediaMetadata, SyncState } from "../types";
import { waitForTransaction } from "./indexedDbTx";

// ===== Database Configuration (Single Source of Truth) =====
export const DB_NAME = "DriveShuffleDB";
export const DB_VERSION = 10; // v10: adds proactive schema recovery for missing stores on Safari/mobile clients

// ===== Store Name Constants (Exported for Consumers) =====
export const STORES = {
  MEDIA: "media",
  AUTH: "auth",
  METADATA: "metadata",
  SYNC_STATE: "syncState",
  LOCAL_FILES: "localFiles",
  PROCESSED_IMAGES: "processedImages",
  IMAGE_METADATA: "imageMetadata",
} as const;

// Legacy constants for backward compatibility (use STORES instead)
const STORE_MEDIA = STORES.MEDIA;
const STORE_AUTH = STORES.AUTH;
const STORE_METADATA = STORES.METADATA;
const STORE_SYNC_STATE = STORES.SYNC_STATE;
const STORE_LOCAL_FILES = STORES.LOCAL_FILES;
const STORE_PROCESSED_IMAGES = STORES.PROCESSED_IMAGES;
const STORE_IMAGE_METADATA = STORES.IMAGE_METADATA;

const REQUIRED_STORES = [
  STORE_MEDIA,
  STORE_AUTH,
  STORE_METADATA,
  STORE_SYNC_STATE,
  STORE_LOCAL_FILES,
  STORE_PROCESSED_IMAGES,
  STORE_IMAGE_METADATA,
  "subscription",
  "usage",
  "usageEvents",
  "apiKeys",
  "apiUsage",
  "searchCache",
  "tagCache",
  "cacheStats",
] as const;

const getMissingStores = (db: IDBDatabase): string[] =>
  REQUIRED_STORES.filter((store) => !db.objectStoreNames.contains(store));

const ensureStoreAndIndexes = (
  db: IDBDatabase,
  transaction: IDBTransaction | null,
  storeName: string,
  options: IDBObjectStoreParameters,
  indexes: Array<{ name: string; keyPath: string | string[]; unique?: boolean }>,
) => {
  let store: IDBObjectStore;
  if (!db.objectStoreNames.contains(storeName)) {
    store = db.createObjectStore(storeName, options);
  } else if (transaction) {
    store = transaction.objectStore(storeName);
  } else {
    return;
  }

  indexes.forEach((index) => {
    if (!store.indexNames.contains(index.name)) {
      store.createIndex(index.name, index.keyPath, { unique: Boolean(index.unique) });
    }
  });
};

const recoverSchema = async (currentVersion: number): Promise<void> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, currentVersion + 1);
    request.onerror = () => reject(request.error);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      const tx = (event.target as IDBOpenDBRequest).transaction;

      ensureStoreAndIndexes(db, tx, STORE_MEDIA, { keyPath: "id" }, []);
      ensureStoreAndIndexes(db, tx, STORE_AUTH, { keyPath: "key" }, []);
      ensureStoreAndIndexes(
        db,
        tx,
        STORE_METADATA,
        { keyPath: "id" },
        [
          { name: "userId", keyPath: "userId" },
          { name: "driveFileId", keyPath: "driveFileId" },
          { name: "localFileHash", keyPath: "localFileHash" },
        ],
      );
      ensureStoreAndIndexes(db, tx, STORE_SYNC_STATE, { keyPath: "userId" }, []);
      ensureStoreAndIndexes(
        db,
        tx,
        STORE_LOCAL_FILES,
        { keyPath: "hash" },
        [{ name: "userId", keyPath: "userId" }],
      );
      ensureStoreAndIndexes(
        db,
        tx,
        STORE_PROCESSED_IMAGES,
        { keyPath: ["imageId", "format", "size", "sourceVersion"] },
        [
          { name: "imageId", keyPath: "imageId" },
          { name: "storedAt", keyPath: "storedAt" },
        ],
      );
      ensureStoreAndIndexes(db, tx, STORE_IMAGE_METADATA, { keyPath: "id" }, []);

      ensureStoreAndIndexes(
        db,
        tx,
        "subscription",
        { keyPath: "userId" },
        [
          { name: "tier", keyPath: "tier" },
          { name: "status", keyPath: "status" },
        ],
      );
      ensureStoreAndIndexes(
        db,
        tx,
        "usage",
        { keyPath: "userId" },
        [{ name: "periodStart", keyPath: "periodStart" }],
      );
      ensureStoreAndIndexes(
        db,
        tx,
        "usageEvents",
        { keyPath: "id" },
        [
          { name: "userId", keyPath: "userId" },
          { name: "timestamp", keyPath: "timestamp" },
        ],
      );
      ensureStoreAndIndexes(
        db,
        tx,
        "apiKeys",
        { keyPath: "id" },
        [
          { name: "userId", keyPath: "userId" },
          { name: "revoked", keyPath: "revoked" },
        ],
      );
      ensureStoreAndIndexes(
        db,
        tx,
        "apiUsage",
        { keyPath: "id" },
        [
          { name: "apiKeyId", keyPath: "apiKeyId" },
          { name: "timestamp", keyPath: "timestamp" },
        ],
      );
      ensureStoreAndIndexes(
        db,
        tx,
        "searchCache",
        { keyPath: "id" },
        [
          { name: "userId", keyPath: "userId" },
          { name: "queryHash", keyPath: "queryHash" },
          { name: "expiresAt", keyPath: "expiresAt" },
          { name: "userQuery", keyPath: ["userId", "queryHash"], unique: true },
        ],
      );
      ensureStoreAndIndexes(
        db,
        tx,
        "tagCache",
        { keyPath: "id" },
        [
          { name: "queryHash", keyPath: "queryHash", unique: true },
          { name: "expiresAt", keyPath: "expiresAt" },
        ],
      );
      ensureStoreAndIndexes(db, tx, "cacheStats", { keyPath: "userId" }, []);
    };
    request.onsuccess = () => {
      request.result.close();
      resolve();
    };
  });

interface AuthData {
  accessToken: string;
  idToken?: string;
  userSub?: string;
  email?: string;
  expiresAt: number;
}

// Check if we're in a browser environment
const isBrowser =
  typeof window !== "undefined" && typeof indexedDB !== "undefined";

export const storageService = {
  async openDB(): Promise<IDBDatabase> {
    if (!isBrowser) {
      throw new Error("IndexedDB requires a browser environment");
    }
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const db = request.result;
        const missing = getMissingStores(db);
        if (missing.length > 0) {
          console.error(
            `[storageService] Missing IndexedDB stores after open: ${missing.join(", ")}`,
          );
          const currentVersion = db.version;
          db.close();
          recoverSchema(currentVersion)
            .then(() => {
              const reopenRequest = indexedDB.open(DB_NAME);
              reopenRequest.onerror = () => reject(reopenRequest.error);
              reopenRequest.onsuccess = () => {
                const recoveredDb = reopenRequest.result;
                const stillMissing = getMissingStores(recoveredDb);
                if (stillMissing.length > 0) {
                  reject(
                    new Error(
                      `IndexedDB schema recovery incomplete. Missing stores: ${stillMissing.join(", ")}`,
                    ),
                  );
                  recoveredDb.close();
                  return;
                }
                resolve(recoveredDb);
              };
            })
            .catch(reject);
          return;
        }
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = (event.target as IDBOpenDBRequest).transaction;
        const oldVersion = event.oldVersion;

        // Helper to ensure index exists on a store
        const ensureIndex = (
          store: IDBObjectStore,
          name: string,
          keyPath: string,
          unique: boolean = false,
        ) => {
          if (!store.indexNames.contains(name)) {
            store.createIndex(name, keyPath, { unique });
          }
        };

        // Create stores and indexes (idempotent - only create if missing)

        // Store: media
        if (!db.objectStoreNames.contains(STORE_MEDIA)) {
          db.createObjectStore(STORE_MEDIA, { keyPath: "id" });
        }

        // Store: auth
        if (!db.objectStoreNames.contains(STORE_AUTH)) {
          db.createObjectStore(STORE_AUTH, { keyPath: "key" });
        }

        // Store: metadata
        if (!db.objectStoreNames.contains(STORE_METADATA)) {
          const metadataStore = db.createObjectStore(STORE_METADATA, {
            keyPath: "id",
          });
          metadataStore.createIndex("userId", "userId", { unique: false });
          metadataStore.createIndex("driveFileId", "driveFileId", {
            unique: false,
          });
          metadataStore.createIndex("localFileHash", "localFileHash", {
            unique: false,
          });
        } else if (transaction) {
          // Store exists, ensure indexes exist
          const metadataStore = transaction.objectStore(STORE_METADATA);
          ensureIndex(metadataStore, "userId", "userId", false);
          ensureIndex(metadataStore, "driveFileId", "driveFileId", false);
          ensureIndex(metadataStore, "localFileHash", "localFileHash", false);
        }

        // Store: syncState
        if (!db.objectStoreNames.contains(STORE_SYNC_STATE)) {
          db.createObjectStore(STORE_SYNC_STATE, { keyPath: "userId" });
        }

        // Store: localFiles
        if (!db.objectStoreNames.contains(STORE_LOCAL_FILES)) {
          const localFilesStore = db.createObjectStore(STORE_LOCAL_FILES, {
            keyPath: "hash",
          });
          localFilesStore.createIndex("userId", "userId", { unique: false });
        } else if (transaction) {
          const localFilesStore = transaction.objectStore(STORE_LOCAL_FILES);
          ensureIndex(localFilesStore, "userId", "userId", false);
        }

        // Store: processedImages (v7)
        const expectedKeyPath = ["imageId", "format", "size", "sourceVersion"];
        if (db.objectStoreNames.contains(STORE_PROCESSED_IMAGES)) {
          const processedImagesStore = transaction?.objectStore(
            STORE_PROCESSED_IMAGES,
          );
          const keyPath = processedImagesStore?.keyPath;
          const matchesKeyPath =
            Array.isArray(keyPath) &&
            keyPath.length === expectedKeyPath.length &&
            keyPath.every((key, index) => key === expectedKeyPath[index]);

          if (!matchesKeyPath) {
            db.deleteObjectStore(STORE_PROCESSED_IMAGES);
          }
        }

        if (!db.objectStoreNames.contains(STORE_PROCESSED_IMAGES)) {
          const processedImagesStore = db.createObjectStore(
            STORE_PROCESSED_IMAGES,
            {
              keyPath: expectedKeyPath,
            },
          );
          processedImagesStore.createIndex("imageId", "imageId", {
            unique: false,
          });
          processedImagesStore.createIndex("storedAt", "storedAt", {
            unique: false,
          });
        } else if (transaction) {
          const processedImagesStore = transaction.objectStore(
            STORE_PROCESSED_IMAGES,
          );
          ensureIndex(processedImagesStore, "imageId", "imageId", false);
          ensureIndex(processedImagesStore, "storedAt", "storedAt", false);
        }

        // Store: imageMetadata (v6)
        if (!db.objectStoreNames.contains(STORE_IMAGE_METADATA)) {
          db.createObjectStore(STORE_IMAGE_METADATA, { keyPath: "id" });
        }

        // Compatibility stores used by other services (subscription/usage/api-keys/search-cache)
        if (!db.objectStoreNames.contains("subscription")) {
          const subStore = db.createObjectStore("subscription", {
            keyPath: "userId",
          });
          subStore.createIndex("tier", "tier", { unique: false });
          subStore.createIndex("status", "status", { unique: false });
        }

        if (!db.objectStoreNames.contains("usage")) {
          const usageStore = db.createObjectStore("usage", {
            keyPath: "userId",
          });
          usageStore.createIndex("periodStart", "periodStart", {
            unique: false,
          });
        }

        if (!db.objectStoreNames.contains("usageEvents")) {
          const usageEventsStore = db.createObjectStore("usageEvents", {
            keyPath: "id",
          });
          usageEventsStore.createIndex("userId", "userId", { unique: false });
          usageEventsStore.createIndex("timestamp", "timestamp", {
            unique: false,
          });
        }

        if (!db.objectStoreNames.contains("apiKeys")) {
          const keysStore = db.createObjectStore("apiKeys", { keyPath: "id" });
          keysStore.createIndex("userId", "userId", { unique: false });
          keysStore.createIndex("revoked", "revoked", { unique: false });
        }

        if (!db.objectStoreNames.contains("apiUsage")) {
          const apiUsageStore = db.createObjectStore("apiUsage", {
            keyPath: "id",
          });
          apiUsageStore.createIndex("apiKeyId", "apiKeyId", { unique: false });
          apiUsageStore.createIndex("timestamp", "timestamp", {
            unique: false,
          });
        }

        if (!db.objectStoreNames.contains("searchCache")) {
          const searchCacheStore = db.createObjectStore("searchCache", {
            keyPath: "id",
          });
          searchCacheStore.createIndex("userId", "userId", { unique: false });
          searchCacheStore.createIndex("queryHash", "queryHash", {
            unique: false,
          });
          searchCacheStore.createIndex("expiresAt", "expiresAt", {
            unique: false,
          });
          searchCacheStore.createIndex("userQuery", ["userId", "queryHash"], {
            unique: true,
          });
        }

        if (!db.objectStoreNames.contains("tagCache")) {
          const tagCacheStore = db.createObjectStore("tagCache", {
            keyPath: "id",
          });
          tagCacheStore.createIndex("queryHash", "queryHash", { unique: true });
          tagCacheStore.createIndex("expiresAt", "expiresAt", {
            unique: false,
          });
        }

        if (!db.objectStoreNames.contains("cacheStats")) {
          db.createObjectStore("cacheStats", { keyPath: "userId" });
        }
      };

      request.onblocked = () => {
        console.warn(
          "IndexedDB upgrade blocked - please close other tabs with this app open",
        );
      };
    });
  },

  async saveAuth(
    auth: {
      accessToken: string;
      idToken?: string;
      userSub?: string;
      email?: string;
    },
    expiresInSeconds: number = 3599,
  ) {
    const db = await this.openDB();
    const tx = db.transaction(STORE_AUTH, "readwrite");
    const store = tx.objectStore(STORE_AUTH);
    const expiresAt = Date.now() + expiresInSeconds * 1000;
    store.put({ key: "session", ...auth, expiresAt });
    return waitForTransaction(tx);
  },

  async getAuth(): Promise<string | null> {
    const session = await this.getAuthSession();
    return session?.accessToken || null;
  },

  async getAuthSession(): Promise<{
    accessToken: string;
    idToken?: string;
    userSub?: string;
    email?: string;
  } | null> {
    try {
      const db = await this.openDB();
      return new Promise((resolve) => {
        try {
          const tx = db.transaction(STORE_AUTH, "readonly");
          const store = tx.objectStore(STORE_AUTH);
          const request = store.get("session");
          request.onsuccess = () => {
            const data = request.result as AuthData;
            if (data && data.expiresAt > Date.now() && data.accessToken) {
              resolve({
                accessToken: data.accessToken,
                idToken: data.idToken,
                userSub: data.userSub,
                email: data.email,
              });
            } else {
              resolve(null);
            }
          };
          request.onerror = () => resolve(null);
        } catch (err) {
          console.warn("Error accessing auth store:", err);
          resolve(null);
        }
      });
    } catch (err) {
      console.warn("Error opening database for auth:", err);
      return null;
    }
  },

  async clearAuth() {
    try {
      const db = await this.openDB();
      // Check if store exists before trying to use it
      if (!db.objectStoreNames.contains(STORE_AUTH)) {
        console.warn("Auth store does not exist, skipping clear");
        return;
      }
      const tx = db.transaction(STORE_AUTH, "readwrite");
      tx.objectStore(STORE_AUTH).delete("session");
      return waitForTransaction(tx);
    } catch (err) {
      console.warn("Error clearing auth:", err);
      // Don't throw - allow logout to continue
    }
  },

  async saveMediaItems(items: MediaItem[]) {
    const db = await this.openDB();
    const tx = db.transaction(STORE_MEDIA, "readwrite");
    const store = tx.objectStore(STORE_MEDIA);

    // Clear existing before saving new set to keep sync simple
    // In a more complex app we would upsert, but here we want to mirror state
    await new Promise<void>((resolve) => {
      const clearReq = store.clear();
      clearReq.onsuccess = () => resolve();
    });

    // Save items. For local files, we must store the file object (Blob)
    items.forEach((item) => {
      store.put(item);
    });

    return waitForTransaction(tx);
  },

  async getMediaItems(): Promise<MediaItem[]> {
    const db = await this.openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_MEDIA, "readonly");
      const store = tx.objectStore(STORE_MEDIA);
      const request = store.getAll();
      request.onsuccess = () => {
        resolve(request.result as MediaItem[]);
      };
      request.onerror = () => resolve([]);
    });
  },

  async clearMedia() {
    try {
      const db = await this.openDB();
      // Check if store exists before trying to use it
      if (!db.objectStoreNames.contains(STORE_MEDIA)) {
        console.warn("Media store does not exist, skipping clear");
        return;
      }
      const tx = db.transaction(STORE_MEDIA, "readwrite");
      tx.objectStore(STORE_MEDIA).clear();
      return waitForTransaction(tx);
    } catch (err) {
      console.warn("Error clearing media:", err);
      // Don't throw - allow logout to continue
    }
  },

  // ===== New Methods for Metadata Storage =====

  /**
   * Save media metadata (separate from file storage)
   */
  async saveMediaMetadata(items: MediaMetadata[]): Promise<void> {
    let db = await this.openDB();
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_METADATA, "readwrite");
    } catch (error: any) {
      if (error?.name !== "NotFoundError") {
        throw error;
      }
      const currentVersion = db.version;
      db.close();
      await recoverSchema(currentVersion);
      db = await this.openDB();
      tx = db.transaction(STORE_METADATA, "readwrite");
    }
    const store = tx.objectStore(STORE_METADATA);

    // Clear existing metadata for this user (if items have userId)
    if (items.length > 0 && items[0].userId) {
      const userId = items[0].userId;
      const index = store.index("userId");
      const request = index.openKeyCursor(IDBKeyRange.only(userId));

      await new Promise<void>((resolve) => {
        request.onsuccess = (e: any) => {
          const cursor = e.target.result;
          if (cursor) {
            store.delete(cursor.primaryKey);
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => resolve();
      });
    }

    // Save new metadata
    items.forEach((item) => {
      store.put(item);
    });

    return waitForTransaction(tx);
  },

  /**
   * Get media metadata
   */
  async getMediaMetadata(userId?: string): Promise<MediaMetadata[]> {
    try {
      const db = await this.openDB();
      return new Promise((resolve) => {
        try {
          const tx = db.transaction(STORE_METADATA, "readonly");
          const store = tx.objectStore(STORE_METADATA);

          if (userId) {
            const index = store.index("userId");
            const request = index.getAll(userId);
            request.onsuccess = () => {
              resolve(request.result as MediaMetadata[]);
            };
            request.onerror = () => resolve([]);
          } else {
            const request = store.getAll();
            request.onsuccess = () => {
              resolve(request.result as MediaMetadata[]);
            };
            request.onerror = () => resolve([]);
          }
        } catch (err) {
          console.warn("Error accessing metadata store:", err);
          resolve([]);
        }
      });
    } catch (err) {
      console.warn("Error opening database for metadata:", err);
      return [];
    }
  },

  /**
   * Save sync state
   */
  async saveSyncState(state: SyncState): Promise<void> {
    const db = await this.openDB();
    const tx = db.transaction(STORE_SYNC_STATE, "readwrite");
    const store = tx.objectStore(STORE_SYNC_STATE);

    if (state.userId) {
      store.put({ ...state, userId: state.userId });
    }

    return waitForTransaction(tx);
  },

  /**
   * Get sync state
   */
  async getSyncState(userId: string): Promise<SyncState | null> {
    try {
      const db = await this.openDB();
      return new Promise((resolve) => {
        try {
          const tx = db.transaction(STORE_SYNC_STATE, "readonly");
          const store = tx.objectStore(STORE_SYNC_STATE);
          const request = store.get(userId);

          request.onsuccess = () => {
            resolve((request.result as SyncState) || null);
          };
          request.onerror = () => resolve(null);
        } catch (err) {
          console.warn("Error accessing sync state store:", err);
          resolve(null);
        }
      });
    } catch (err) {
      console.warn("Error opening database for sync state:", err);
      return null;
    }
  },

  /**
   * Save local file blob
   */
  async saveLocalFile(
    hash: string,
    file: File,
    userId?: string,
  ): Promise<void> {
    const db = await this.openDB();
    const tx = db.transaction(STORE_LOCAL_FILES, "readwrite");
    const store = tx.objectStore(STORE_LOCAL_FILES);

    store.put({
      hash,
      file: file as any, // IndexedDB can store File objects
      userId,
      savedAt: Date.now(),
    });

    return waitForTransaction(tx);
  },

  /**
   * Get local file blob
   */
  async getLocalFile(hash: string): Promise<File | null> {
    const db = await this.openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_LOCAL_FILES, "readonly");
      const store = tx.objectStore(STORE_LOCAL_FILES);
      const request = store.get(hash);

      request.onsuccess = () => {
        const result = request.result as { file?: File } | undefined;
        if (result && result.file instanceof File) {
          resolve(result.file);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  },

  /**
   * Delete local file
   */
  async deleteLocalFile(hash: string): Promise<void> {
    const db = await this.openDB();
    const tx = db.transaction(STORE_LOCAL_FILES, "readwrite");
    const store = tx.objectStore(STORE_LOCAL_FILES);
    store.delete(hash);
    return waitForTransaction(tx);
  },

  /**
   * Clear local-only file bytes and metadata for the current user.
   * This keeps remote metadata (Drive/Dropbox) intact for rehydration.
   */
  async clearLocalData(userId?: string): Promise<void> {
    const db = await this.openDB();

    if (db.objectStoreNames.contains(STORE_LOCAL_FILES)) {
      const tx = db.transaction(STORE_LOCAL_FILES, "readwrite");
      const store = tx.objectStore(STORE_LOCAL_FILES);

      if (userId && store.indexNames.contains("userId")) {
        const index = store.index("userId");
        const cursorReq = index.openCursor(IDBKeyRange.only(userId));
        await new Promise<void>((resolve) => {
          cursorReq.onsuccess = (event: any) => {
            const cursor = event.target.result as IDBCursorWithValue | null;
            if (cursor) {
              store.delete(cursor.primaryKey);
              cursor.continue();
            } else {
              resolve();
            }
          };
          cursorReq.onerror = () => resolve();
        });
      } else {
        store.clear();
      }
      await waitForTransaction(tx);
    }

    if (db.objectStoreNames.contains(STORE_METADATA)) {
      const tx = db.transaction(STORE_METADATA, "readwrite");
      const store = tx.objectStore(STORE_METADATA);
      const cursorReq = userId && store.indexNames.contains("userId")
        ? store.index("userId").openCursor(IDBKeyRange.only(userId))
        : store.openCursor();

      await new Promise<void>((resolve) => {
        cursorReq.onsuccess = (event: any) => {
          const cursor = event.target.result as IDBCursorWithValue | null;
          if (cursor) {
            const value = cursor.value as MediaMetadata;
            if (value?.source === "local") {
              store.delete(cursor.primaryKey);
            }
            cursor.continue();
          } else {
            resolve();
          }
        };
        cursorReq.onerror = () => resolve();
      });
      await waitForTransaction(tx);
    }
  },

  /**
   * Calculate file hash (SHA-256)
   */
  async calculateFileHash(file: File): Promise<string> {
    // Use Web Crypto API for hashing
    const arrayBuffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  },

  /**
   * Assert that required stores exist (for defensive checks)
   * Logs warnings in dev, useful for debugging schema issues
   */
  async assertStoresExist(storeNames: string[]): Promise<boolean> {
    try {
      const db = await this.openDB();
      const missing: string[] = [];

      for (const storeName of storeNames) {
        if (!db.objectStoreNames.contains(storeName)) {
          missing.push(storeName);
        }
      }

      if (missing.length > 0) {
        console.error(`[storageService] Missing stores: ${missing.join(", ")}`);
        return false;
      }

      return true;
    } catch (err) {
      console.error("[storageService] Failed to assert stores:", err);
      return false;
    }
  },
};
